/**
 * Doğum Günü servisi.
 *
 * Spec madde 9.3:
 *  - BOSS her personel için bir yılın doğum günü katsayısını belirler
 *  - Katsayı: Yok | 1.5 | 2
 *  - Personel o gün giriş yapınca konfeti + kutlama + katsayı maaşa yansır
 *
 * Veri modeli (Firestore koleksiyonu: dogumGunuKatsayilari):
 *   id = personelId + '_' + yil    (deterministik, override için)
 *   personelId, yil, katsayi, notlar?, olusturanId, olusturmaTarihi
 */

import {
  collection, doc, getDoc, getDocs, setDoc, deleteDoc,
  query, where,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { FsTimestamp, IsoTarih } from '@/types'

const COLL = 'dogumGunuKatsayilari'

/** Katsayı seçenekleri */
export type DogumKatsayisi = 1.5 | 2

export const DOGUM_KATSAYI_ETIKETLERI: Record<string, string> = {
  '1.5': 'x1.5 (Bayram ücreti)',
  '2': 'x2 (Çifte ücret)',
}

export interface DogumGunuKatsayisi {
  id: string                  // = personelId + '_' + yil
  personelId: string
  yil: number
  katsayi: DogumKatsayisi
  notlar?: string
  olusturanId: string
  olusturmaTarihi: FsTimestamp
}

// ═══════════════════════════════════════════════════════════════
// CRUD
// ═══════════════════════════════════════════════════════════════

function docId(personelId: string, yil: number): string {
  return `${personelId}_${yil}`
}

/** BOSS personel için katsayı set eder (override). */
export async function katsayiSetle(
  personelId: string,
  yil: number,
  katsayi: DogumKatsayisi,
  olusturanId: string,
  notlar?: string
): Promise<void> {
  const db = getFirestoreDb()
  const data: Record<string, unknown> = {
    personelId,
    yil,
    katsayi,
    olusturanId,
    olusturmaTarihi: nowServer(),
  }
  if (notlar?.trim()) data.notlar = notlar.trim()
  await setDoc(doc(db, COLL, docId(personelId, yil)), data)
}

/** Belirli yıl için kaldır (artık "yok" demek) */
export async function katsayiKaldir(personelId: string, yil: number): Promise<void> {
  const db = getFirestoreDb()
  await deleteDoc(doc(db, COLL, docId(personelId, yil)))
}

/** Bir personelin o yılki katsayısı (yoksa null) */
export async function katsayiGetir(
  personelId: string,
  yil: number
): Promise<DogumGunuKatsayisi | null> {
  const db = getFirestoreDb()
  const snap = await getDoc(doc(db, COLL, docId(personelId, yil)))
  if (!snap.exists()) return null
  return { id: snap.id, ...(snap.data() as Omit<DogumGunuKatsayisi, 'id'>) }
}

/** O yıl için tanımlı tüm katsayılar */
export async function yilKatsayilariGetir(yil: number): Promise<DogumGunuKatsayisi[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, COLL), where('yil', '==', yil))
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<DogumGunuKatsayisi, 'id'>),
  }))
}

// ═══════════════════════════════════════════════════════════════
// DOĞUM GÜNÜ TARİH HESAPLAMALARI
// ═══════════════════════════════════════════════════════════════

/**
 * dogumTarihi ISO (YYYY-MM-DD) → bu yıl için doğum gününün ISO tarihini döner.
 * 29 Şubat doğanlar için 28 Şubat olarak kabul edilir (yıl artık değilse).
 */
export function buYilDogumGunu(dogumTarihi: IsoTarih, yil?: number): IsoTarih {
  const hedefYil = yil ?? new Date().getFullYear()
  const [, ay, gun] = dogumTarihi.split('-').map(Number)

  // 29 Şubat kontrolü
  let aktifGun = gun
  if (ay === 2 && gun === 29) {
    const artikYil = (hedefYil % 4 === 0 && hedefYil % 100 !== 0) || hedefYil % 400 === 0
    if (!artikYil) aktifGun = 28
  }
  return `${hedefYil}-${String(ay).padStart(2, '0')}-${String(aktifGun).padStart(2, '0')}` as IsoTarih
}

/** Bugün doğum günü mü? */
export function bugunDogumGunuMu(dogumTarihi: IsoTarih): boolean {
  const bugun = new Date()
  const bugunIso = bugun.toISOString().substring(0, 10)
  return buYilDogumGunu(dogumTarihi, bugun.getFullYear()) === bugunIso
}

/**
 * Doğum gününe kalan gün sayısı (negatif = geçti).
 * 0 = bugün, 1 = yarın, vb.
 */
export function dogumGununeKalanGun(dogumTarihi: IsoTarih): number {
  const bugun = new Date()
  bugun.setHours(0, 0, 0, 0)
  const hedef = new Date(buYilDogumGunu(dogumTarihi, bugun.getFullYear()) + 'T00:00:00')
  const fark = hedef.getTime() - bugun.getTime()
  return Math.round(fark / (1000 * 60 * 60 * 24))
}

/**
 * Personel listesinden yaklaşan doğum günlerini sırala (önümüzdeki gunSiniri gün içinde).
 * Geçmiş bu yıl olanlar dışlanır.
 */
export interface YaklasanDogumGunu<T> {
  personel: T
  dogumGunuIso: IsoTarih
  kalanGun: number
}

export function yaklasanDogumGunleri<T extends { dogumTarihi?: IsoTarih }>(
  personeller: T[],
  gunSiniri: number = 30
): YaklasanDogumGunu<T>[] {
  return personeller
    .filter((p) => p.dogumTarihi)
    .map((p) => ({
      personel: p,
      dogumGunuIso: buYilDogumGunu(p.dogumTarihi!),
      kalanGun: dogumGununeKalanGun(p.dogumTarihi!),
    }))
    .filter((x) => x.kalanGun >= 0 && x.kalanGun <= gunSiniri)
    .sort((a, b) => a.kalanGun - b.kalanGun)
}
